import React from 'react';
import { SPECIES_NONE_TOKEN, speciesFilterChipLabel } from './utils';

export interface SpeciesFilterRowProps {
  suggestedSpecies: string[];
  /** True if current result set has at least one image with no species */
  hasImagesWithNoSpecies: boolean;
  includeSpecies: string[];
  includeSpeciesInput: string;
  setIncludeSpeciesInput: (v: string) => void;
  addIncludeSpecies: (species: string) => void;
  removeIncludeSpecies: (species: string) => void;
  includeSpeciesSuggestions: string[];
  excludeSpecies: string[];
  excludeSpeciesInput: string;
  setExcludeSpeciesInput: (v: string) => void;
  addExcludeSpecies: (species: string) => void;
  removeExcludeSpecies: (species: string) => void;
  excludeSpeciesSuggestions: string[];
}

const SpeciesFilterRow: React.FC<SpeciesFilterRowProps> = ({
  suggestedSpecies,
  hasImagesWithNoSpecies,
  includeSpecies,
  includeSpeciesInput,
  setIncludeSpeciesInput,
  addIncludeSpecies,
  removeIncludeSpecies,
  includeSpeciesSuggestions,
  excludeSpecies,
  excludeSpeciesInput,
  setExcludeSpeciesInput,
  addExcludeSpecies,
  removeExcludeSpecies,
  excludeSpeciesSuggestions,
}) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', width: '100%' }}>
    <div style={{ fontSize: '0.9rem', color: '#444', fontWeight: 600 }}>Filter by species</div>
    <p style={{ margin: 0, fontSize: '0.8rem', color: '#666', maxWidth: '720px' }}>
      Narrow the grid by species (separate from tags). Include = show only images whose species is one of the
      listed values. Exclude = hide those species. You can combine with tag filters above.
    </p>
    <div style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: '1.5rem' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', minWidth: 0, maxWidth: '320px' }}>
        <span style={{ fontSize: '0.85rem', color: '#555' }}>Include species</span>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: '0.35rem',
            padding: '0.4rem',
            border: '1px solid #dee2e6',
            borderRadius: '4px',
            background: '#fff',
          }}
        >
          {includeSpecies.map((s) => (
            <span
              key={s}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.2rem',
                padding: '0.2rem 0.4rem',
                fontSize: '0.85rem',
                background: '#e8f5e9',
                border: '1px solid #a5d6a7',
                borderRadius: '4px',
              }}
            >
              {speciesFilterChipLabel(s)}
              <button
                type="button"
                onClick={() => removeIncludeSpecies(s)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, lineHeight: 1, color: '#666' }}
                aria-label={`Remove ${speciesFilterChipLabel(s)}`}
              >
                ×
              </button>
            </span>
          ))}
          <input
            type="text"
            value={includeSpeciesInput}
            onChange={(e) => setIncludeSpeciesInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                e.stopPropagation();
                addIncludeSpecies(includeSpeciesInput);
              }
            }}
            placeholder="Type species, Enter to add…"
            style={{ border: 'none', outline: 'none', padding: '0.2rem', fontSize: '0.9rem', minWidth: '120px', flex: '1 1 120px' }}
          />
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem', marginTop: '0.2rem', alignItems: 'center' }}>
          <span style={{ fontSize: '0.8rem', color: '#666', marginRight: '0.3rem' }}>Suggestions:</span>
          {suggestedSpecies.length === 0 && !hasImagesWithNoSpecies ? (
            <span style={{ fontSize: '0.8rem', color: '#999' }}>No species in this result set</span>
          ) : (
            <>
              {hasImagesWithNoSpecies && !includeSpecies.includes(SPECIES_NONE_TOKEN) && (
                <button
                  type="button"
                  onClick={() => addIncludeSpecies(SPECIES_NONE_TOKEN)}
                  style={{ padding: '0.2rem 0.4rem', fontSize: '0.8rem', border: '1px solid #ccc', borderRadius: '4px', background: '#fff', cursor: 'pointer' }}
                >
                  (no species)
                </button>
              )}
              {includeSpeciesSuggestions.length > 0 ? (
                includeSpeciesSuggestions.slice(0, 20).map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => addIncludeSpecies(s)}
                    style={{ padding: '0.2rem 0.4rem', fontSize: '0.8rem', border: '1px solid #ccc', borderRadius: '4px', background: '#fff', cursor: 'pointer' }}
                  >
                    {s}
                  </button>
                ))
              ) : suggestedSpecies.length > 0 ? (
                <span style={{ fontSize: '0.8rem', color: '#999' }}>All listed species selected</span>
              ) : null}
            </>
          )}
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', minWidth: 0, maxWidth: '320px' }}>
        <span style={{ fontSize: '0.85rem', color: '#555' }}>Exclude species</span>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: '0.35rem',
            padding: '0.4rem',
            border: '1px solid #dee2e6',
            borderRadius: '4px',
            background: '#fff',
          }}
        >
          {excludeSpecies.map((s) => (
            <span
              key={s}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.2rem',
                padding: '0.2rem 0.4rem',
                fontSize: '0.85rem',
                background: '#fff3e0',
                border: '1px solid #ffcc80',
                borderRadius: '4px',
              }}
            >
              {speciesFilterChipLabel(s)}
              <button
                type="button"
                onClick={() => removeExcludeSpecies(s)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, lineHeight: 1, color: '#666' }}
                aria-label={`Remove exclude ${speciesFilterChipLabel(s)}`}
              >
                ×
              </button>
            </span>
          ))}
          <input
            type="text"
            value={excludeSpeciesInput}
            onChange={(e) => setExcludeSpeciesInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                e.stopPropagation();
                addExcludeSpecies(excludeSpeciesInput);
              }
            }}
            placeholder="Type species, Enter to add…"
            style={{ border: 'none', outline: 'none', padding: '0.2rem', fontSize: '0.9rem', minWidth: '120px', flex: '1 1 120px' }}
          />
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem', marginTop: '0.2rem', alignItems: 'center' }}>
          <span style={{ fontSize: '0.8rem', color: '#666', marginRight: '0.3rem' }}>Suggestions:</span>
          {suggestedSpecies.length === 0 && !hasImagesWithNoSpecies ? (
            <span style={{ fontSize: '0.8rem', color: '#999' }}>No species in this result set</span>
          ) : (
            <>
              {hasImagesWithNoSpecies && !excludeSpecies.includes(SPECIES_NONE_TOKEN) && (
                <button
                  type="button"
                  onClick={() => addExcludeSpecies(SPECIES_NONE_TOKEN)}
                  style={{ padding: '0.2rem 0.4rem', fontSize: '0.8rem', border: '1px solid #ccc', borderRadius: '4px', background: '#fff', cursor: 'pointer' }}
                >
                  (no species)
                </button>
              )}
              {excludeSpeciesSuggestions.length > 0 ? (
                excludeSpeciesSuggestions.slice(0, 20).map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => addExcludeSpecies(s)}
                    style={{ padding: '0.2rem 0.4rem', fontSize: '0.8rem', border: '1px solid #ccc', borderRadius: '4px', background: '#fff', cursor: 'pointer' }}
                  >
                    {s}
                  </button>
                ))
              ) : suggestedSpecies.length > 0 ? (
                <span style={{ fontSize: '0.8rem', color: '#999' }}>All listed species selected</span>
              ) : null}
            </>
          )}
        </div>
      </div>
    </div>
  </div>
);

export default SpeciesFilterRow;
